import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Achievement, UserAchievement, AchievementProgress, AchievementStats } from '@/types/achievements';
import { toast } from 'sonner';
import { AchievementNotification } from '@/components/AchievementNotification';

export const useAchievements = () => {
  return useQuery({
    queryKey: ['achievements'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('achievements')
        .select('*')
        .order('sort_order');
      
      if (error) throw error;
      return data as Achievement[];
    },
    staleTime: 1000 * 60 * 60, // 1 hour
  });
};

export const useUserAchievements = (userId?: string) => {
  const queryClient = useQueryClient();
  const [showNotification, setShowNotification] = useState<{ achievement: Achievement; xp: number } | null>(null);

  const query = useQuery({
    queryKey: ['user-achievements', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_achievements')
        .select('*, achievement:achievements(*)')
        .eq('user_id', userId!)
        .order('unlocked_at', { ascending: false });
      
      if (error) throw error;
      return data as UserAchievement[];
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Set up real-time subscription for achievement unlocks
  useEffect(() => {
    if (!userId) return;

    const channel = supabase
      .channel('user-achievements-realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'user_achievements',
          filter: `user_id=eq.${userId}`
        },
        async (payload) => {
          console.log('[Achievements] New achievement unlocked:', payload);
          
          // Fetch the full achievement details
          const { data: achievement } = await supabase
            .from('achievements')
            .select('*')
            .eq('id', payload.new.achievement_id)
            .single();

          if (achievement) {
            // Show celebration notification
            setShowNotification({
              achievement: achievement as Achievement,
              xp: achievement.xp_reward
            });

            // Invalidate queries to refresh data
            queryClient.invalidateQueries({ queryKey: ['user-achievements', userId] });
            queryClient.invalidateQueries({ queryKey: ['achievement-progress', userId] });
            queryClient.invalidateQueries({ queryKey: ['achievement-stats', userId] });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, queryClient]);

  return {
    ...query,
    notification: showNotification,
    clearNotification: () => setShowNotification(null),
  };
};

export const useAchievementProgress = (userId?: string) => {
  return useQuery({
    queryKey: ['achievement-progress', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('achievement_progress')
        .select('*, achievement:achievements(*)')
        .eq('user_id', userId!);
      
      if (error) throw error;
      return data as AchievementProgress[];
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 5,
  });
};

export const useAchievementStats = (userId?: string) => {
  return useQuery({
    queryKey: ['achievement-stats', userId],
    queryFn: async () => {
      // Get achievement-specific stats
      const { data: achievementStats, error: statsError } = await supabase
        .rpc('get_user_achievement_stats', { target_user_id: userId! })
        .single();
      
      if (statsError) throw statsError;
      
      // Get total XP, level, and level_title from profile
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('xp_points, level, level_title')
        .eq('id', userId!)
        .single();
      
      if (profileError) throw profileError;
      
      return {
        ...achievementStats,
        total_xp: profile.xp_points || 0,
        level: profile.level || 1,
        level_title: profile.level_title || 'Bookworm Beginner'
      } as AchievementStats;
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 5,
  });
};

export const useCheckAchievements = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      console.log('[useCheckAchievements] Checking achievements for user:', userId);
      const { data, error } = await supabase.functions.invoke('check-achievements', {
        body: { userId },
      });

      if (error) {
        console.error('[useCheckAchievements] Error:', error);
        throw error;
      }
      
      console.log('[useCheckAchievements] Check complete:', data);
      return data;
    },
    onSuccess: (data, userId) => {
      console.log('[useCheckAchievements] Invalidating queries after successful check');
      queryClient.invalidateQueries({ queryKey: ['user-achievements', userId] });
      queryClient.invalidateQueries({ queryKey: ['achievement-progress', userId] });
      queryClient.invalidateQueries({ queryKey: ['achievement-stats', userId] });
      
      if (data?.newAchievements?.length > 0) {
        console.log(`[useCheckAchievements] ${data.newAchievements.length} new achievements unlocked!`);
        data.newAchievements.forEach((achievement: Achievement) => {
          toast.success(`🎉 Achievement Unlocked: ${achievement.name}!`, {
            description: `+${achievement.xp_reward} XP`,
            duration: 5000,
          });
        });
      } else {
        console.log('[useCheckAchievements] No new achievements unlocked');
      }
    },
    onError: (error) => {
      console.error('[useCheckAchievements] Mutation error:', error);
    },
  });
};
